import { ChangeEvent, useEffect, useState } from 'react';
import {
  Button, Checkboxes, Col, Container, Row, Select, SummaryList, TextInput,
  Card,
  Radios,
} from 'nhsuk-react-components';
import { CohortMetadata } from 'schemas/metadata/cohorts.ts';
import { capitaliseFirstChar, YYYYMMDDHHMMSSmmToHumanReadable } from 'utils/formattingUtils.ts';
import './Cohorts.scss';
import { IterationCohort } from 'schemas/IterationCohort.schema.ts';
import { getFilteredCohorts, getCohortStatus } from 'utils/cohortUtils.ts';
import { IterationRule } from 'schemas/IterationRule.schema.ts';
import CohortRuleErrorMessage from './CohortRuleErrorMessage';

interface CohortsFormProps {
  onSuccessfulSubmission: (newCohorts: IterationCohort[]) => void;
  existingCohorts: string[];
  campaignTarget: string;
  cohorts : CohortMetadata[]
  existingRules: IterationRule[];
  isSubmitting?: boolean;
}

export default function CohortsForm({
  onSuccessfulSubmission, existingCohorts, campaignTarget, cohorts, existingRules, isSubmitting = false,
}: CohortsFormProps) {
  const initialAvailableCohorts = cohorts
    .map((cohort) => cohort.CohortLabel);

  const [availableCohorts, setAvailableCohorts] = useState<string[]>(initialAvailableCohorts);
  const [selectableCohorts, setSelectableCohorts] = useState<string[]>(initialAvailableCohorts);
  const [selectedCohorts, setSelectedCohorts] = useState<string[]>(existingCohorts);

  const [maxCohortAge, setMaxCohortAge] = useState<string>('3');
  const [latestVersion, setLatestVersion] = useState<string>('Y');
  const [wordsFilter, setWordsFilter] = useState<string>('');
  const [cohortLabelInRule, setCohortLabelInRule] = useState('');
  const [ruleErrorSource, setRuleErrorSource] = useState<'checkbox' | 'removeButton' | null>(null);

  const handleCheckboxChange = (e: ChangeEvent<HTMLInputElement>) => {
    setCohortLabelInRule('');
    setRuleErrorSource(null);

    const { value, checked } = e.target;
    const cohortUsedInRules = existingRules.some((rule) => rule.CohortLabel === value);

    if (cohortUsedInRules) {
      setRuleErrorSource('checkbox');
      setCohortLabelInRule(value);
      return;
    }

    if (checked) {
      setSelectedCohorts([...selectedCohorts, value]);
    } else {
      setSelectedCohorts(selectedCohorts.filter((cohort) => cohort !== value));
    }
  };

  const handleRemove = (cohortLabel: string) => {
    setCohortLabelInRule('');
    setRuleErrorSource(null);

    const cohortUsedInRules = existingRules.some((rule) => rule.CohortLabel === cohortLabel);

    if (cohortUsedInRules) {
      const removeButton = document.getElementById(`remove-cohort-${cohortLabel}`);
      if (removeButton) {
        removeButton.blur();
      }
      setRuleErrorSource('removeButton');
      setCohortLabelInRule(cohortLabel);
    } else {
      setSelectedCohorts(selectedCohorts.filter((selectedCohort) => selectedCohort !== cohortLabel));
      if (!availableCohorts.includes(cohortLabel)) {
        setAvailableCohorts([...availableCohorts, cohortLabel]);
      }
    }
  };

  const handleMove = (selectedCohort: string, direction: 'UP' | 'DOWN') => {
    setCohortLabelInRule('');
    setRuleErrorSource(null);

    const currentIndex = selectedCohorts.findIndex((cohort) => cohort === selectedCohort);

    if ((direction === 'UP' && currentIndex === 0) || (direction === 'DOWN' && currentIndex === selectedCohorts.length)) return;

    const newIndex = direction === 'UP' ? currentIndex - 1 : currentIndex + 1;

    const newCohorts = selectedCohorts.map((item, index) => {
      if (index === newIndex) return selectedCohorts[currentIndex];
      if (index === currentIndex) return selectedCohorts[newIndex];
      return item;
    });

    setSelectedCohorts(newCohorts);
  };

  const handleSubmit = () => {
    const newCohorts: IterationCohort[] = [];

    selectedCohorts.forEach((selectedCohort, index) => {
      newCohorts.push({
        CohortLabel: selectedCohort,
        Priority: index * 10,
      });
    });

    onSuccessfulSubmission(newCohorts);
  };

  const getMemberCount = (cohortLabel: string): string => {
    const foundCohort = cohorts.find((cohort) => cohort.CohortLabel === cohortLabel);

    if (foundCohort?.MemberCount) {
      return foundCohort.MemberCount.toLocaleString('en-GB', {
        maximumFractionDigits: 0,
      });
    }

    return 'N/A';
  };

  const getRefreshedDate = (cohortLabel: string): string => {
    const foundCohort = cohorts.find((cohort) => cohort.CohortLabel === cohortLabel);

    if (foundCohort?.LastRefreshed) {
      return YYYYMMDDHHMMSSmmToHumanReadable(
        foundCohort.LastRefreshed.toString(),
      );
    }

    return 'N/A';
  };

  const updateSelectableCohorts = (): void => {
    const newSelectableCohorts = getFilteredCohorts({
      cohorts,
      maxAge: parseInt(maxCohortAge, 10),
      latestVersion: latestVersion === 'Y',
      wordsFilter,
    });

    setSelectableCohorts(newSelectableCohorts.map((cohort) => cohort.CohortLabel));
  };

  useEffect(() => {
    updateSelectableCohorts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxCohortAge, latestVersion, wordsFilter]);

  useEffect(() => {
    setWordsFilter(campaignTarget);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="cohorts-wrapper">

      <Card>
        <div className="nhsuk-u-padding-bottom-1">
          <Container>
            <Row>
              <Col width="full">
                <h3 className="nhsuk-u-padding-bottom-3 nhsuk-u-font-weight-bold">
                  The cohorts you can choose from are applied by default. You can change these default settings here.
                </h3>
              </Col>
            </Row>
            <Row>
              <Col width="full">
                <div>
                  <Select
                    id="version-select"
                    test-id="cohort-age-select"
                    label="Hide cohorts that have not been refreshed for:"
                    onChange={(e) => setMaxCohortAge((e.target as HTMLSelectElement).value)}
                    value={maxCohortAge}
                  >
                    <Select.Option value="3" key="3 Months">3 months</Select.Option>
                    <Select.Option value="6" key="6 Months">6 months</Select.Option>
                    <Select.Option value="12" key="12 Months">12 months</Select.Option>
                    <Select.Option value="24" key="24 Months">24 months</Select.Option>
                    <Select.Option value="" key="All">No restriction</Select.Option>
                  </Select>
                </div>
              </Col>
            </Row>
            <Row>
              <Col width="full">
                <p>
                  Only show the latest version of each cohort.
                </p>
                <Radios onChange={(e) => setLatestVersion((e.target as HTMLInputElement).value)} name="latestVersion" inline>
                  <Radios.Item value="Y" id="latest-version-yes" defaultChecked>Yes</Radios.Item>
                  <Radios.Item value="N" id="latest-version-no">No</Radios.Item>
                </Radios>
              </Col>
            </Row>
            <Row>
              <Col width="full">
                <TextInput
                  label="Start typing below to find a cohort. You can remove cohorts from the list by prefixing a word with a minus sign, for example ‘-COVID’. If you leave this field blank, you will see all available cohorts in the list. "
                  test-id="text-filter"
                  onChange={(e) => setWordsFilter((e.target as HTMLInputElement).value)}
                  value={wordsFilter}
                />
              </Col>
            </Row>
          </Container>
          <p className="nhsuk-u-font-weight-bold">Add one or more cohort</p>
          <p>Choose which cohorts to use for this iteration. Only people included in a cohort will be invited for a vaccination</p>
          <Container>
            <Row>
              <Col width="full">
                <form>
                  {selectableCohorts.length === 0 ? (
                    <div className="nhsuk-u-font-weight-bold">No cohorts available</div>
                  ) : (
                    <Checkboxes
                      onChange={handleCheckboxChange}
                      error={ruleErrorSource === 'checkbox' ? (
                        <CohortRuleErrorMessage cohortLabel={cohortLabelInRule} />
                      ) : undefined}
                    >
                      {selectedCohorts.length === 0 && (
                        <div className="nhsuk-u-font-weight-bold">No cohorts selected</div>
                      )}
                      {selectableCohorts.sort().map((cohort) => {
                        const cohortStatus = getCohortStatus(cohort, cohorts);
                        return (
                          <Checkboxes.Item
                            key={cohort}
                            value={cohort}
                            checked={selectedCohorts.includes(cohort)}
                          >
                            <div>
                              <span className="nhsuk-u-font-weight-bold">{cohort}</span>
                              {cohortStatus === 'ok' ? '' : (
                                <span className="nhsuk-u-secondary-text-colour nhsuk-u-font-size-14">
                                  {' '}
                                  (
                                  {capitaliseFirstChar(cohortStatus)}
                                  )
                                </span>
                              )}
                            </div>
                            <div className="nhsuk-u-secondary-text-colour nhsuk-u-font-weight-bold nhsuk-u-font-size-14">
                              People in cohort:
                              <span className="nhsuk-u-padding-right-2" />
                              {getMemberCount(cohort)}
                            </div>
                            <div className="nhsuk-u-secondary-text-colour nhsuk-u-font-weight-bold nhsuk-u-font-size-14">
                              Data last received:
                              <span className="nhsuk-u-padding-right-2" />
                              {getRefreshedDate(cohort)}
                            </div>
                          </Checkboxes.Item>
                        );
                      })}
                    </Checkboxes>
                  )}
                </form>
              </Col>
            </Row>
          </Container>
          {selectedCohorts.length > 0 && (
            <div className="selected-cohorts">
              <Card cardType="non-urgent">
                <Card.Heading>
                  Prioritise chosen cohorts:
                </Card.Heading>
                <Container>
                  <Row>
                    <Col width="full">
                      <SummaryList className="cohorts-table">
                        {selectedCohorts.map((selectedCohort, idx) => {
                          const cohortStatus = getCohortStatus(selectedCohort, cohorts);
                          return (
                            <SummaryList.Row key={selectedCohort} id={selectedCohort}>
                              <SummaryList.Key>
                                Cohort
                                {' '}
                                {idx + 1}
                                {cohortStatus === 'ok' ? '' : (
                                  <span className="nhsuk-u-secondary-text-colour nhsuk-u-font-size-14">
                                    {' '}
                                    (
                                    {capitaliseFirstChar(cohortStatus)}
                                    )
                                  </span>
                                )}
                              </SummaryList.Key>
                              <SummaryList.Value>
                                <div className="nhsuk-u-margin-bottom-3">
                                  <b id={`cohort-${selectedCohort}`}>{selectedCohort}</b>
                                </div>
                                <div className="detail-wrapper">
                                  <b className="detail-key">People in cohort:</b>
                                  <span className="nhsuk-u-padding-right-2" />
                                  {getMemberCount(selectedCohort)}
                                </div>
                                <div className="detail-wrapper">
                                  <b className="detail-key">Data last received: </b>
                                  <span className="nhsuk-u-padding-right-2" />
                                  {getRefreshedDate(selectedCohort)}
                                </div>

                                {ruleErrorSource === 'removeButton' && cohortLabelInRule === selectedCohort && (
                                  <CohortRuleErrorMessage cohortLabel={selectedCohort} />
                                )}

                              </SummaryList.Value>
                              <SummaryList.Action>
                                {selectedCohorts.length > 1 && (
                                  <>
                                    {idx !== 0 && (
                                      <button
                                        type="button"
                                        className="nhsuk-back-link__link nhsuk-u-margin-bottom-2"
                                        onClick={() => handleMove(selectedCohort, 'UP')}
                                        id={`up-cohort-${selectedCohort}`}
                                        aria-label={`Move up Cohort ${idx + 1} ${selectedCohort}`}
                                      >
                                        Move up
                                      </button>
                                    )}
                                    {idx !== selectedCohorts.length - 1 && (
                                      <button
                                        type="button"
                                        className="nhsuk-back-link__link nhsuk-u-margin-bottom-2"
                                        onClick={() => handleMove(selectedCohort, 'DOWN')}
                                        id={`down-cohort-${selectedCohort}`}
                                        aria-label={`Move down Cohort ${idx + 1} ${selectedCohort}`}
                                      >
                                        Move&nbsp;down
                                      </button>
                                    )}
                                  </>
                                )}
                                <div />
                                <button
                                  type="button"
                                  className="nhsuk-back-link__link nhsuk-u-margin-bottom-2 delete-button"
                                  onClick={() => handleRemove(selectedCohort)}
                                  id={`remove-cohort-${selectedCohort}`}
                                  aria-label={`Remove Cohort ${idx + 1} ${selectedCohort}`}
                                >
                                  Remove
                                </button>
                              </SummaryList.Action>
                            </SummaryList.Row>
                          );
                        })}
                      </SummaryList>
                    </Col>
                  </Row>
                </Container>
              </Card>
            </div>
          )}
        </div>
      </Card>
      <Button onClick={handleSubmit} className="nhsuk-u-margin-right-5" disabled={isSubmitting}>
        {isSubmitting ? 'Updating...' : 'Save and continue'}
      </Button>
    </div>
  );
}
